import { FC, useEffect, useContext, useRef, useState } from "react";
import { LinearProgress, List, ListItem, ListItemText, Tooltip } from "@mui/material";
import MButton from "components/momentum/MButton";
import GridEventProvider from "components/context/GridEventContext";
import ArrowPopper from "components/app/ArrowPopper";
import { GirdViewFunction } from "../GridViewFunctionBar";
import { Account, FavoriteEventDetail } from "types/portfolio";
import { useAxios } from "myAxios";
import {
  checkSelectAll,
  dispatchCustomEvent,
  getBulkActionAccounts,
  trackButton,
  transformAgGridRequest,
} from "utils";
import { GridApi } from "ag-grid-community";
import { toast } from "react-toastify";

const FavoriteFeature: FC<GirdViewFunction> = ({
  anchorEl,
  open,
  onClick,
  ready,
  onClose,
  selectedRows,
}) => {
  const axios = useAxios();

  const [totalCount, setTotalCount] = useState(0);

  const { gridEvent } = useContext(GridEventProvider);
  const epPopRef = useRef<HTMLDivElement>(null);
  const epCellRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const dataLoadedHandler = (e: any) => {
      const { totalCount: total } = e.detail;
      setTotalCount(total);
    };
    addEventListener("DATA_LOADED", dataLoadedHandler);
    return () => {
      removeEventListener("DATA_LOADED", dataLoadedHandler);
    };
  }, []);

  useEffect(() => {
    const closePop = (e: Event) => {
      if (
        open &&
        !epPopRef.current?.contains(e.target as Node) &&
        epPopRef.current !== e.target &&
        !epCellRef.current?.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, [open]);

  const handleItemClick = (type: "add" | "remove") => async () => {
    trackButton(type === "add" ? "Favorite_Add to Favorite" : "Favorite_Remove from Favorite");
    const selectAll = checkSelectAll();
    const selectedRows: Account[] | undefined = gridEvent?.api.getSelectedRows();
    if (!selectedRows?.length) {
      toast.error("Please select an account first!");
      return;
    }
    if ((selectAll && totalCount > 1000) || selectedRows.length > 1000) {
      toast.error("Maximum limit - 1000 Accounts");
      return;
    }
    let accountKeys: string[];
    let toastId: React.ReactText = "";
    if (selectAll) {
      toastId = toast.info(
        <>
          <p>Operating bulk action, this might take a while</p>
          <LinearProgress
            color="primary"
            style={{ borderRadius: 5 }}
          />
        </>,
        {
          position: "top-right",
          autoClose: false,
        }
      );
      const filterModel = gridEvent?.api.getFilterModel();
      const { data } = await getBulkActionAccounts<Account[]>(
        "/api/portfolio/accounts",
        "POST",
        undefined,
        transformAgGridRequest({ filterModel })
      );
      accountKeys = data.map((x) => x.accountKey);
    } else {
      accountKeys = selectedRows.map((x) => x.accountKey);
    }
    if (type === "remove") {
      try {
        await axios.delete("/api/portfolio/favorite", { data: accountKeys });
        dispatchCustomEvent<FavoriteEventDetail>("FAVORITE_CHANGE", {
          type: "REMOVE",
          payload: accountKeys,
        });
        favoriteCallback(gridEvent?.api);
        toast.dismiss(toastId);

        onClose();
      } catch {
        toast.error("Fail to add to favorite");
        if (toastId) toast.dismiss(toastId);
      }
    } else {
      try {
        await axios.post("/api/portfolio/favorite", accountKeys);
        dispatchCustomEvent<FavoriteEventDetail>("FAVORITE_CHANGE", {
          type: "ADD",
          payload: accountKeys,
        });
        toast.dismiss(toastId);
        onClose();
      } catch {
        toast.error("Fail to remove from favorite");
        if (toastId) toast.dismiss(toastId);
      }
    }
    gridEvent?.api.forEachNode((n) => {
      if (accountKeys.includes(n.id)) {
        n.setData({ ...n.data, favorite: type === "add" });
      }
    });
  };

  return (
    <>
      <div ref={epCellRef}>
        <Tooltip
          disableHoverListener={!!!selectedRows}
          disableFocusListener={!!!selectedRows}
          disableTouchListener={!!!selectedRows}
          open={showTooltip}
          arrow={true}
          title={
            <div>
              <b>Disabled until account(s) selected</b>
            </div>
          }
        >
          <span
            onMouseOverCapture={!selectedRows ? () => setShowTooltip(true) : undefined}
            onMouseOutCapture={!selectedRows ? () => setShowTooltip(false) : undefined}
            style={{ marginRight: 5 }}
          >
            <MButton
              onClick={onClick}
              disabled={!ready || !selectedRows}
            >
              Favorite
            </MButton>
          </span>
        </Tooltip>
      </div>
      <ArrowPopper
        open={open}
        anchorEl={anchorEl}
        disableFlip={true}
        containerStyle={{ padding: 0 }}
      >
        <div ref={epPopRef}>
          <List dense={true}>
            <ListItem
              button={true}
              onClick={handleItemClick("add")}
            >
              <ListItemText
                primary={"Add to Favorite"}
                style={{ fontSize: 12 }}
              />
            </ListItem>
            <ListItem
              button={true}
              onClick={handleItemClick("remove")}
            >
              <ListItemText
                primary={"Remove from Favorite"}
                style={{ fontSize: 12 }}
              />
            </ListItem>
          </List>
        </div>
      </ArrowPopper>
    </>
  );
};

export const favoriteCallback = (api?: GridApi) => {
  let active = false;
  const filterModel = api?.getFilterModel();
  if (filterModel?.externalFilter?.filter === "Favorites") {
    active = true;
  }
  if (active) {
    api?.setFilterModel(api?.getFilterModel());
  }
};

export default FavoriteFeature;
